import { useEffect, useState } from "react";

/**
 * Loads data asynchronously from a native module indicating whether the data is still loading.
 *
 * Has two possible states, either we are loading and the data is null or the data is loaded.
 * If the promise rejects, loading becomes false and the error is thrown on the next render.
 *
 * @returns A tuple of loading state and data state: [isLoading, data].
 * @param method - The (possibly null) method to load the data from
 */
export function usePromise<T>(method: () => Promise<T>): [true, null] | [false, T] {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    method()
      .then((data) => {
        if (!cancelled) {
          setData(data);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [method]);

  // Throw error in render phase so error boundary can catch it
  if (error) {
    throw error;
  }

  if (isLoading) {
    return [true, null];
  }

  return [false, data as T];
}
